import { A, useLocation } from '@solidjs/router'
import { type Component, createSignal, For, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import Container from '~/components/Container'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import { useAuth } from '~/contexts/AuthContext'
import { SITE } from '~/data/site'
import ThemeToggle from './ThemeToggle'

interface NavbarProps {
  onToggleMobileMenu?: (isOpen: boolean) => void
  onComponentsMenuClick?: () => void
  showBreadcrumb?: boolean
}

interface MenuItem {
  href: string
  title: string
  description: string
  icon: string
}

interface ContactItem {
  href: string
  title: string
  description: string
  icon: string
  colorScheme: 'green' | 'blue'
  external?: boolean
}

// Datos de navegación
const menuItems: MenuItem[] = [
  {
    href: '/',
    title: 'Inicio',
    description: 'Página principal',
    icon: 'material-symbols-light:home-outline'
  },
  {
    href: '/componentes',
    title: 'Componentes',
    description: 'UI Library',
    icon: 'material-symbols-light:code'
  }
]

const contactItems: ContactItem[] = [
  {
    href: SITE.contact.phoneHref,
    title: 'Llamar',
    description: SITE.contact.phone,
    icon: 'material-symbols-light:call-outline',
    colorScheme: 'green'
  },
  {
    href: SITE.contact.emailHref,
    title: 'Email',
    description: SITE.contact.email,
    icon: 'material-symbols-light:mail-outline',
    colorScheme: 'blue'
  },
  {
    href: SITE.contact.whatsapp,
    title: 'WhatsApp',
    description: 'Mensaje directo',
    icon: 'mdi:whatsapp',
    colorScheme: 'green',
    external: true
  }
]

// Componente optimizado para items de menú móvil (basado en DrawerItem pero simplificado)
const MenuItemComponent = ({ item, onClose }: { item: MenuItem; onClose: () => void }) => {
  const handleClick = () => {
    onClose()
  }

  const classes = twMerge('group w-full inline-flex items-center rounded-lg transition-colors select-none px-3 py-2.5 cursor-pointer', 'text-foreground-muted hover:bg-surface-hover', 'gap-4')

  const Content = (
    <div class={classes} onClick={handleClick}>
      {/* Icono principal */}
      <Icon icon={item.icon} size={32} class="text-foreground-subtle" />

      {/* Texto */}
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-foreground">{item.title}</div>
        <div class="truncate text-xs text-foreground-subtle">{item.description}</div>
      </div>
    </div>
  )

  return (
    <A href={item.href} class="block">
      {Content}
    </A>
  )
}

// Componente reutilizable para items de contacto
const ContactItemComponent = ({ item }: { item: ContactItem }) => {
  const colorClasses = {
    green: 'text-success',
    blue: 'text-info'
  }

  const additionalProps = item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {}

  return (
    <a href={item.href} class="group flex items-center gap-3 p-3 rounded-lg hover:bg-surface-hover/70 transition-all duration-200" {...additionalProps}>
      <Icon icon={item.icon} size={20} class={colorClasses[item.colorScheme]} />
      <div class="flex-1">
        <div class="text-sm font-medium text-foreground">{item.title}</div>
        <div class="text-xs text-foreground-subtle">{item.description}</div>
      </div>
    </a>
  )
}

// Componente para el header del menú móvil
const MobileMenuHeader = () => {
  return (
    <div class="p-6 pb-0">
      <div class="flex items-center gap-3 mb-2">
        <div class="p-2 rounded-xl bg-linear-to-br from-primary-500/10 to-primary-600/5">
          <Icon icon="edens:logo" class="text-primary-500" size={24} />
        </div>
        <div>
          <h2 class="font-bold text-foreground">Florez Jaramillo</h2>
          <p class="text-xs text-foreground-subtle">Soluciones Tecnológicas</p>
        </div>
      </div>
    </div>
  )
}

// Componente para la navegación principal y contacto
const MobileMenuNavigation = ({ onClose }: { onClose: () => void }) => {
  const { isAuthenticated } = useAuth()

  return (
    <div class="p-6">
      {/* Navegación principal */}
      <nav class="flex flex-col gap-2 mb-8">
        <div class="text-xs font-semibold text-foreground-subtle uppercase tracking-wider mb-3 px-3">Navegación</div>
        <For each={menuItems}>{item => <MenuItemComponent item={item} onClose={onClose} />}</For>

        {/* El login vive solo en el header de escritorio; en móvil va aquí,
            junto al resto de la navegación, para no quedarse sin acceso. */}
        <Show when={!isAuthenticated()}>
          <A href="/login" onClick={onClose} class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-muted hover:text-primary transition-colors">
            <Icon icon="material-symbols-light:login" size={22} class="shrink-0" />
            Iniciar Sesión
          </A>
        </Show>
      </nav>

      {/* Sección de contacto */}
      <div class="border-t border-border pt-6">
        <div class="text-xs font-semibold text-foreground-subtle uppercase tracking-wider mb-4 px-3">Contáctanos</div>
        <div class="grid grid-cols-1 gap-3">
          <For each={contactItems}>{item => <ContactItemComponent item={item} />}</For>
        </div>
      </div>
    </div>
  )
}

// Componente para el footer del menú móvil
const MobileMenuFooter = () => {
  return (
    <div class="p-6 pt-0">
      <div class="text-center">
        <p class="text-xs text-foreground-subtle">© 2025 Florez Jaramillo Solutions</p>
        <p class="text-xs text-foreground-subtle mt-1">Innovación tecnológica</p>
      </div>
    </div>
  )
}

// Contenido del menú principal que aparecerá en el Drawer (responsive left)
const MobileMenuContent = ({ onClose }: { onClose: () => void }) => {
  return (
    <div class="h-svh grid grid-rows-[auto_1fr_auto]">
      <MobileMenuHeader />
      <div class="overflow-y-auto">
        <MobileMenuNavigation onClose={onClose} />
      </div>
      <MobileMenuFooter />
    </div>
  )
}

const Link = ({ href = '#/', ...props }: { title: string; href?: string; icon?: string }) => {
  const location = useLocation()

  const isActive = () => {
    try {
      // Si el href incluye hash, compararlo con pathname + hash
      if (href.includes('#')) {
        const [path, hash] = href.split('#')
        const current = location.pathname + (location.hash || '')
        return (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)) && current.endsWith(`#${hash}`)
      }
      if (href === '/') return location.pathname === '/'
      return location.pathname.startsWith(href)
    } catch {
      return false
    }
  }

  // Reactivo: la clase debe recalcularse al cambiar de ruta
  const classes = () =>
    twMerge(
      'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-primary-600 after:transition-all after:duration-300',
      isActive() ? 'text-primary after:w-full' : 'text-foreground-muted hover:text-primary'
    )

  return (
    <A href={href} class={classes()}>
      <Show when={props.icon}>{icon => <Icon icon={icon()} size={22} class="shrink-0" />}</Show>
      {props.title}
    </A>
  )
}

const Navbar: Component<NavbarProps> = _props => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = createSignal(false)

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen()
    setIsMobileMenuOpen(newState)
    _props.onToggleMobileMenu?.(newState)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    _props.onToggleMobileMenu?.(false)
  }

  const isComponentsRoute = () => location.pathname.startsWith('/componentes')

  const componentName = () => {
    const slug = location.pathname.replace('/componentes', '').split('/').filter(Boolean)
    if (slug.length === 0) return ''
    return slug[slug.length - 1].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  return (
    <>
      <header class="flex justify-center sticky min-h-18 max-h-18 top-0 z-50 bg-surface/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-border">
        <Container class="flex h-18 items-center">
          {/* Left Section */}
          <div class="flex items-center gap-3">
            {/* Mobile: Menu Button */}
            <Button icon="edens:menu" variant="text" color="secondary" iconSize={32} onClick={toggleMobileMenu} class="md:hidden" />

            <Show
              when={_props.showBreadcrumb && isComponentsRoute()}
              fallback={
                <>
                  {/* Desktop: Logo */}
                  <A href="/" class="hidden md:flex items-center gap-3 group">
                    <div class="relative p-2 rounded-xl bg-linear-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25 group-hover:shadow-primary-500/40 transition-all duration-300 group-hover:scale-105">
                      <Icon icon="edens:logo" class="text-white drop-shadow-sm" size={24} />
                    </div>
                    <div class="flex flex-col leading-tight">
                      <span class="text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-200">Florez Jaramillo</span>
                      <span class="text-xs text-foreground-subtle font-medium">Soluciones Tecnológicas</span>
                    </div>
                  </A>
                  {/* Mobile: Brand Name */}
                  <A href="/" class="md:hidden group">
                    <h1 class="text-lg font-bold text-foreground group-hover:text-primary transition-all duration-300 tracking-wide">FLOREZ JARAMILLO</h1>
                  </A>
                </>
              }
            >
              {/* Breadcrumb */}
              <nav class="hidden md:flex items-center gap-2 text-sm">
                <A href="/" class="text-foreground-subtle hover:text-primary transition-colors">
                  <Icon icon="material-symbols-light:home-outline" size={22} />
                </A>
                <Icon icon="material-symbols-light:chevron-right" size={16} class="text-foreground-subtle/50" />
                <A href="/componentes" class="text-foreground-subtle hover:text-primary transition-colors font-medium">
                  Componentes
                </A>
                <Show when={componentName()}>
                  <Icon icon="material-symbols-light:chevron-right" size={16} class="text-foreground-subtle/50" />
                  <span class="text-primary font-semibold">{componentName()}</span>
                </Show>
              </nav>
            </Show>
          </div>

          {/* Center Section */}
          <div class="flex-1 flex justify-center">
            {/* Con breadcrumb la navegación central sobra: repite Inicio y Componentes. */}
            <Show when={!(_props.showBreadcrumb && isComponentsRoute())}>
              <nav class="hidden md:flex items-center gap-1">
                <For each={menuItems}>{item => <Link title={item.title} href={item.href} icon={item.icon} />}</For>
              </nav>
            </Show>
          </div>

          {/* Right Section */}
          <div class="flex items-center gap-1">
            <Show when={!isAuthenticated()}>
              <A
                href="/login"
                class="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground hover:text-primary dark:hover:text-primary transition-colors rounded-lg hover:bg-surface-muted"
              >
                <Icon icon="material-symbols-light:login" size={22} class="shrink-0" />
                Iniciar Sesión
              </A>
            </Show>
            <Show when={_props.onComponentsMenuClick}>
              <Button icon="material-symbols-light:widgets-outline" variant="text" color="secondary" iconSize={28} onClick={_props.onComponentsMenuClick} class="lg:hidden" />
            </Show>
            <ThemeToggle />
          </div>
        </Container>
      </header>

      {/* Drawer para el menú móvil (lado izquierdo) */}
      <Drawer isOpen={isMobileMenuOpen()} position="left" onClose={closeMobileMenu} enableGestures={true}>
        <MobileMenuContent onClose={closeMobileMenu} />
      </Drawer>
    </>
  )
}

export default Navbar
